import { _EVENT_NAMES } from '../AnalyticsEvents'
import { ORDER_ITEM_TYPE, TILE_CONTENT } from '@mixtiles/web-backend-shared'
import { getShortSizeName } from '../../SizesManager'
import { analytics, EVENT_NAMES } from '../Analytics'
import { getTilesMaterials } from '../../tileMaterial'
import { MIXED_MATERIALS } from '../../../pages/PhotoStyler/TileDesignerConsts'
import countBy from 'lodash/countBy'
import sessionStorageManager from '../../SessionStorageManager'
import { getNonEmptyCollagePhotos } from '../../Collage/collageService'
import { getPortfolioVariant } from '../../Collage/collagesPortfolio'
import { ServerSideAnalyticsPlatform } from '../ServerSideAnalytics'
import { PRODUCT_TYPES } from '../../ProductTypeState'

export const CARDS_VIEWED = 'cardsViewed'

/**
 * @param {{
 *   cloudServiceName?: string | null,
 *   context?: string,
 *   pickedPhotosCount?: number,
 *   tileContent?: string,
 *   additionalEventProperties?: Record<string, any>,
 * }} [params]
 */
export function trackPhotoPicked({
  cloudServiceName,
  context,
  pickedPhotosCount,
  tileContent,
  additionalEventProperties = {},
} = {}) {
  const eventProperties = {
    'Photo Type': cloudServiceName ? 'Cloud Photo' : 'Device Photo',
    'Tile Content': tileContent,
    context,
    ...additionalEventProperties,
  }

  if (cloudServiceName) {
    eventProperties['Cloud Service'] = cloudServiceName
  }

  if (pickedPhotosCount) {
    eventProperties['Picked Photos Count'] = pickedPhotosCount
  }

  // TODO: add more properties
  analytics.trackWithOneTimeServerSide(
    EVENT_NAMES.PHOTO_PICKED,
    eventProperties,
    [ServerSideAnalyticsPlatform.Braze, ServerSideAnalyticsPlatform.Tiktok]
  )
}

export function getViewType(productType) {
  if (productType === PRODUCT_TYPES.PHOTO_BOOK) {
    return { 'View Type': 'Photo Book' }
  }

  return { 'View Type': 'Tile' }
}

export function getTileContent(tile) {
  if (!tile) {
    return {}
  }

  return { 'Tile Content': tile.tileContent }
}

export function getTileDistribution(tiles) {
  const distribution = countBy(tiles, (tile) => tile.tileContent)
  // Add missing tile contents as 0
  Object.values(TILE_CONTENT).forEach((tileContent) => {
    if (!distribution[tileContent]) {
      distribution[tileContent] = 0
    }
  })

  const totalTiles = tiles.length
  const distributionWithPercentages = {}

  Object.entries(distribution).forEach(([key, value]) => {
    distributionWithPercentages[`${key} Count`] = value
    distributionWithPercentages[`${key} Count Share`] =
      totalTiles > 0 ? Math.round((value / totalTiles) * 100) : 0
  })

  return {
    'Tiles Count': totalTiles,
    ...distributionWithPercentages,
  }
}

export function getCollageProperties(tile) {
  if (tile?.collage) {
    const { collage, materialType, tileSize } = tile
    const nonEmptyPhotosCount = getNonEmptyCollagePhotos(
      Object.values(collage?.photos || [])
    ).length
    const containsText = !!(
      collage?.collageText?.showText && collage?.collageText?.text
    )
    return {
      'Photos Count': nonEmptyPhotosCount,
      'Expected Photos Count': collage?.layout?.photosCount,
      'Empty Photos Count': collage?.layout?.photosCount - nonEmptyPhotosCount,
      'Current Layout': collage?.layout?.style,
      'Contains Text': containsText,
      'Text Style': collage?.collageText?.textStyleId || 'none',
      'Collage Text': containsText ? collage?.collageText?.text : 'none',
      'Current Style: Material': materialType,
      'Current Style: Size': tileSize,
    }
  } else {
    return {}
  }
}

export function getLayoutChangedProperties(
  tile,
  previousLayout,
  currentLayout,
  previousPhotosCount,
  currentPhotosCount
) {
  let reason
  if (currentLayout.style !== previousLayout.style) {
    reason = 'Layout Tapped'
  } else if (currentLayout.tileSize !== previousLayout.tileSize) {
    reason = 'Size Changed'
  } else {
    reason =
      previousPhotosCount > currentPhotosCount
        ? 'Photos Removed'
        : 'Photos Added'
  }

  const { collage, materialType } = tile
  const portfolioStyles = getPortfolioVariant().styles
  return {
    'Previous Layout': previousLayout.style,
    'Previous Layout Index': portfolioStyles.indexOf(previousLayout.style),
    'Current Layout': currentLayout.style,
    'Current Layout Index': portfolioStyles.indexOf(currentLayout.style),
    'Photos Count - Previous Layout': previousPhotosCount,
    'Photos Count - Current Layout': currentPhotosCount,
    'Empty Photos Count - Previous Layout':
      previousLayout.photosCount - previousPhotosCount,
    'Empty Photos Count - Current Layout':
      currentLayout.photosCount - currentPhotosCount,
    'Expected Photos Count - Previous Layout': previousLayout.photosCount,
    'Expected Photos Count - Current Layout': currentLayout.photosCount,
    Reason: reason,
    'Contains Text': !!(
      collage?.collageText?.showText && collage?.collageText?.text
    ),
    'Current Style: Material': materialType,
  }
}

export function getMaterialProperties(materialType, tiles) {
  const materials = getTilesMaterials(tiles)
  return {
    // Fall back to the tiles' material when no explicit style is passed
    'Current Style: Material':
      materialType ?? (materials.length === 1 ? materials[0] : MIXED_MATERIALS),
    'Material Count': materials.length,
  }
}

export function trackOldPhotoRemoved({ photoUploadTime, source }) {
  analytics.track(_EVENT_NAMES.REMOVED_OLD_PHOTO, {
    'Photo Upload Time': photoUploadTime,
    Source: source,
  })
}

export function sendProductCardEvent(
  eventName,
  { categoryIndex, cardIndex, source, ...otherEventProperties }
) {
  analytics.track(eventName, {
    'Category Index': categoryIndex ? categoryIndex + 1 : null, // Increase by 1 so it won't start from index 0
    'Card Index': cardIndex + 1, // Increase by 1 so it won't start from index 0
    Source: source,
    ...otherEventProperties,
  })
}

export function sendProductCardViewedEvent(cardAnalytics, cardsViewed) {
  if (!cardsViewed.includes(cardAnalytics['Product ID'])) {
    sendProductCardEvent(EVENT_NAMES.PRODUCT_CARD_VIEWED, cardAnalytics)
    cardsViewed = [...cardsViewed, cardAnalytics['Product ID']]
    sessionStorageManager.set(CARDS_VIEWED, cardsViewed)
  }
  return cardsViewed
}

export function generateCategoryProperties(category) {
  return {
    'Category ID': category.id,
    'Category Name': category.name,
  }
}

function generateProductEventProperties({
  productId,
  productName,
  productType,
  wallName,
}) {
  return {
    'Product ID': productId,
    'Product Name': productName,
    'Product Type': productType,
    'Wall Name': wallName,
  }
}

export function generatePhotoWallEventProperties(wallData) {
  return generateProductEventProperties({
    productId: wallData._id,
    productName: wallData.title,
    wallName: wallData.title,
    productType: ORDER_ITEM_TYPE.PHOTO_WALL,
  })
}

export function generateSingleTilesEventProperties(tileSize) {
  return generateProductEventProperties({
    productId: getShortSizeName(tileSize).toLowerCase(),
    productName: getShortSizeName(tileSize).toLowerCase(),
    productType: ORDER_ITEM_TYPE.SINGLE_TILES,
  })
}
